import { Link } from 'react-router-dom';
import useScrollMotion from '../../hooks/useScrollMotion';
import usePageMeta from '../../hooks/usePageMeta';
import {
  CEO_QUOTE,
  CTA,
  HERO,
  META,
  SECTION_HEADS,
  SERVICES,
  SERVICES_INTRO,
  SERVICES_NOTE,
  WHO_WE_ARE,
  type Service,
} from '../../content/site';
import CtaBand from '../../components/CtaBand/CtaBand';
import Approach from '../../components/sections/Approach';
import Audiences from '../../components/sections/Audiences';
import SectionHeader from '../../components/sections/SectionHeader';
import Why from '../../components/sections/Why';
import { ArrowIcon, PinIcon } from '../../components/Icons/Icons';
import m from '../../styles/marketing.module.css';
import styles from './Home.module.css';

/** Home is the whole story: who we are, services, approach, why us, and who
 *  we serve. The old About page folded in here; /about lands on #who-we-are. */
const Home = () => {
  usePageMeta(null, META.homeDescription);
  const root = useScrollMotion<HTMLDivElement>();

  return (
    <div ref={root}>
      {/* Full-bleed navy hero after the strategy houses: the tower photo
          fills the right and fades into navy under the copy. */}
      <header className={styles.hero}>
        <div className={styles.heroMedia} aria-hidden="true">
          <img
            src="/images/empire-tower-up.jpg"
            alt=""
            data-parallax="0.04"
            fetchPriority="high"
          />
        </div>
        <div className={styles.heroShade} />

        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <div className={`${m.eyebrowRule} ${styles.heroEyebrow} ${styles.rise}`}>
              {HERO.eyebrow}
            </div>
            <h1 className={`${styles.heroHeadline} ${styles.rise}`} data-d="1">
              {HERO.headline}
            </h1>
            <p className={`${styles.heroLead} ${styles.rise}`} data-d="2">
              {HERO.lead}
            </p>
            <div className={`${styles.heroActions} ${styles.rise}`} data-d="3">
              <Link to="/contact" className={`${m.btn} ${m.btnLg} ${m.btnLight}`}>
                {HERO.primary} <ArrowIcon width={18} height={18} />
              </Link>
              <a href="#services" className={`${m.btn} ${m.btnLg} ${m.btnGhostLight}`}>
                {HERO.secondary} <ArrowIcon width={18} height={18} />
              </a>
            </div>
          </div>

          {/* The practice strip — Bain's topic row, pointing at the service rows. */}
          <nav className={`${styles.strip} ${styles.rise}`} data-d="4" aria-label="Our practices">
            <ol className={styles.stripList}>
              {SERVICES.map((service, i) => (
                <li key={service.slug}>
                  <a href={`#${service.slug}`} className={styles.stripLink}>
                    <span>{String(i + 1).padStart(2, '0')}</span>
                    {service.short}
                  </a>
                </li>
              ))}
            </ol>
            <div className={styles.stripPlace}>
              <PinIcon width={14} height={14} /> Empire Tower, Bangkok
            </div>
          </nav>
        </div>
      </header>

      <section className={`${m.section} ${m.tint}`} id="who-we-are">
        <div className={m.container}>
          <SectionHeader {...SECTION_HEADS.whoWeAre} />
          <div className={m.storyGrid}>
            <div className={m.storyMedia} data-reveal>
              <div className={`${m.photo} ${m.cutLeft} ${styles.storyPhoto}`}>
                <img
                  src="/images/empire-view-office.webp"
                  alt="The Bangkok skyline and MahaNakhon from Empire Tower"
                  data-parallax="0.04"
                  loading="lazy"
                />
              </div>
            </div>
            <div className={styles.prose} data-reveal data-reveal-delay="90">
              <p className={styles.proseLead}>{WHO_WE_ARE[0]}</p>
              <p>{WHO_WE_ARE[1]}</p>
              {/* The Group CEO's line, word for word from the original site. */}
              <figure className={styles.proseQuote}>
                <blockquote>
                  <p>“{CEO_QUOTE.quote}”</p>
                </blockquote>
                <figcaption>{CEO_QUOTE.attribution}</figcaption>
              </figure>
              <a href="#approach" className={`${m.btn} ${m.btnMd} ${m.btnOutline}`}>
                How we work <ArrowIcon width={16} height={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className={m.section} id="services">
        <div className={m.container}>
          <SectionHeader {...SECTION_HEADS.services} />
          <div className={styles.practice}>
            {/* Sticky shell; the reveal transform goes on the inner wrapper. */}
            <aside className={styles.practiceIntro}>
              <div className={styles.practiceIntroInner} data-reveal>
                <p className={styles.practiceLead}>{SERVICES_INTRO}</p>
                <p className={styles.practiceNote}>{SERVICES_NOTE}</p>
                <Link to="/contact" className={`${m.btn} ${m.btnMd} ${m.btnOutline}`}>
                  Discuss your mandate <ArrowIcon width={16} height={16} />
                </Link>
                {/* Fills the sticky column while the rows scroll; no parallax, the
                    column is already moving. Hidden once the column stacks. */}
                <div className={`${m.photo} ${m.cutLeft} ${styles.practicePhoto}`}>
                  <img
                    src="/images/empire-view-down.webp"
                    alt="Looking down on the BTS and the Chong Nonsi skywalk from Empire Tower"
                    loading="lazy"
                  />
                </div>
              </div>
            </aside>
            <ol className={styles.practiceList}>
              {SERVICES.map((service, i) => (
                <ServiceRow key={service.slug} service={service} index={i} />
              ))}
            </ol>
          </div>
        </div>
      </section>

      <Approach tint />

      <Why />

      <Audiences tint />

      <CtaBand title={CTA.title} lead={CTA.lead} />
    </div>
  );
};

const ServiceRow = ({ service, index }: { service: Service; index: number }) => (
  <li className={styles.practiceRow} data-reveal data-reveal-delay={index * 70}>
    <article id={service.slug} className={styles.practiceArticle}>
      <div className={styles.practiceIndex}>{String(index + 1).padStart(2, '0')}</div>
      <div>
        <h3 className={styles.practiceTitle}>{service.title}</h3>
        <p className={styles.practiceBody}>{service.body}</p>
        <ul className={styles.practiceScope} aria-label="Includes">
          {service.scope.map((tag) => (
            <li key={tag}>{tag}</li>
          ))}
        </ul>
      </div>
    </article>
  </li>
);

export default Home;
